import type {
  UpdateUserPreferences,
  UserPreferences,
} from '@src/dto/UserPreferences';
import {
  getEnabledUploadProviders,
  isUploadProviderEnabled,
} from './providerHelpers';

const DEFAULT_UPLOAD_PROVIDER = 'r2' as const;
const CURRENT_UPLOAD_PROVIDER_MIGRATION_VERSION = 2;

export function getDefaultUploadProvider(): UserPreferences['uploadProvider'] {
  const enabledProviders = getEnabledUploadProviders();

  if (enabledProviders.includes(DEFAULT_UPLOAD_PROVIDER)) {
    return DEFAULT_UPLOAD_PROVIDER;
  }

  return enabledProviders[0] ?? DEFAULT_UPLOAD_PROVIDER;
}

export function getDefaultUserPreferences(): UserPreferences {
  return {
    uploadProvider: getDefaultUploadProvider(),
    hasExplicitlySetProvider: false,
    uploadProviderMigrationVersion: CURRENT_UPLOAD_PROVIDER_MIGRATION_VERSION,
  };
}

function isUploadProvider(
  value: unknown,
): value is UserPreferences['uploadProvider'] {
  return value === 'ardrive' || value === 's3' || value === 'r2';
}

export function normalizeUserPreferences(input: unknown): UserPreferences {
  const defaultUploadProvider = getDefaultUploadProvider();

  if (!input || typeof input !== 'object') {
    return getDefaultUserPreferences();
  }

  const candidate = input as Partial<UserPreferences>;
  const uploadProvider = isUploadProvider(candidate.uploadProvider)
    ? candidate.uploadProvider
    : defaultUploadProvider;
  const hasExplicitlySetProvider =
    typeof candidate.hasExplicitlySetProvider === 'boolean'
      ? candidate.hasExplicitlySetProvider
      : false;
  const resolvedUploadProvider = isUploadProviderEnabled(uploadProvider)
    ? uploadProvider
    : defaultUploadProvider;
  const version =
    typeof candidate.uploadProviderMigrationVersion === 'number' &&
    Number.isInteger(candidate.uploadProviderMigrationVersion) &&
    candidate.uploadProviderMigrationVersion > 0
      ? candidate.uploadProviderMigrationVersion
      : 1;

  if (version < CURRENT_UPLOAD_PROVIDER_MIGRATION_VERSION) {
    return {
      uploadProvider: defaultUploadProvider,
      hasExplicitlySetProvider:
        uploadProvider === defaultUploadProvider
          ? hasExplicitlySetProvider
          : false,
      uploadProviderMigrationVersion: CURRENT_UPLOAD_PROVIDER_MIGRATION_VERSION,
    };
  }

  return {
    uploadProvider: resolvedUploadProvider,
    hasExplicitlySetProvider:
      resolvedUploadProvider === uploadProvider
        ? hasExplicitlySetProvider
        : false,
    uploadProviderMigrationVersion: CURRENT_UPLOAD_PROVIDER_MIGRATION_VERSION,
  };
}

export function applyUserPreferenceUpdates(
  current: unknown,
  updates: UpdateUserPreferences,
): UserPreferences {
  const preferences = normalizeUserPreferences(current);

  if (updates.uploadProvider === undefined) {
    return preferences;
  }

  return {
    uploadProvider: updates.uploadProvider,
    hasExplicitlySetProvider: true,
    uploadProviderMigrationVersion: CURRENT_UPLOAD_PROVIDER_MIGRATION_VERSION,
  };
}
